// src/pages/caisse/StatsCaisse.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Card, Row, Col, Button, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
    Chart as ChartJS,
    CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend,
    ArcElement, PointElement, LineElement
} from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';
import Footer from "../../layouts/Footer";
import Header from "../../layouts/Header";
import { formatCurrency } from "../../utils/formatters";
import { API_BASE_URL } from "../../constants/api";
import { COLORS } from "../../constants/colors";

ChartJS.register(
    CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend,
    ArcElement, PointElement, LineElement
);

/* ============================================================
   TOAST
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 5000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   HELPERS
   ============================================================ */
// ⭐ Parser une date MySQL de façon fiable
const parseMySQLDate = (dateStr) => {
    if (!dateStr) return null;
    const safeStr = String(dateStr).replace(' ', 'T');
    const d = new Date(safeStr);
    return isNaN(d.getTime()) ? null : d;
};

// ⭐ Calculer le vrai reste d'une opération
// = montant_initial - avance_initial - somme(paiements enfants)
const computeOperationTotals = (op) => {
    const montantInitial = parseFloat(op.montant_initial ?? op.montant ?? 0);
    const avanceInitiale = parseFloat(op.avance ?? 0);

    const paiements = Array.isArray(op.paiements) ? op.paiements : [];
    const sommePaiements = paiements.reduce((s, p) => s + (parseFloat(p.avance ?? p.montant ?? 0)), 0);

    const totalVerse = avanceInitiale + sommePaiements;
    const resteReel = Math.max(0, montantInitial - totalVerse);

    return {
        montantInitial,
        avanceInitiale,
        sommePaiements,
        totalVerse,
        resteReel,
        nbPaiements: paiements.length,
        pourcentage: montantInitial > 0 ? Math.round((totalVerse / montantInitial) * 100) : 0
    };
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function StatsCaisse() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [operations, setOperations] = useState([]);
    const [period, setPeriod] = useState('all');
    const [toast, setToast] = useState({ show: false, type: '', title: '', message: '' });

    const showToast = useCallback((type, title, message) =>
        setToast({ show: true, type, title, message }), []);

    const hideToast = useCallback(() =>
        setToast({ show: false, type: '', title: '', message: '' }), []);

    const fetchOperations = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/caisse.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error("Erreur chargement");

            const result = await response.json();
            console.log("📦 Réponse API caisse:", result);

            let dataArray = [];
            if (Array.isArray(result)) {
                dataArray = result;
            } else if (result.success && Array.isArray(result.data)) {
                dataArray = result.data;
            } else if (Array.isArray(result.data)) {
                dataArray = result.data;
            }

            const initialOps = dataArray.filter(op =>
                op.type === 'initial' || (!op.parent_id && op.parent_id !== 0)
            );

            console.log("📊 Opérations parentes:", initialOps.length);
            console.log("📊 Détail opérations:", initialOps.map(o => ({
                id: o.id,
                nom: o.beneficiaire_nom,
                montant_initial: o.montant_initial,
                avance: o.avance,
                paiements_count: o.paiements?.length || 0,
                reste_reel: o.reste_reel
            })));

            setOperations(initialOps);

            if (initialOps.length === 0) {
                showToast('info', 'Information', 'Aucune opération de caisse trouvée');
            }
        } catch (err) {
            console.error("Erreur fetchOperations:", err);
            setError(err.message);
            showToast('error', 'Erreur', err.message);
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    useEffect(() => {
        fetchOperations();
    }, [fetchOperations]);

    /* ============================================================
       FILTRES PAR PÉRIODE
       ============================================================ */
    const filteredOps = useMemo(() => {
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth();

        return operations.filter(op => {
            const opDate = parseMySQLDate(op.created_at);
            if (!opDate) return period === 'all';

            if (period === 'month') {
                return opDate.getFullYear() === currentYear
                    && opDate.getMonth() === currentMonth;
            }
            if (period === 'year') {
                return opDate.getFullYear() === currentYear;
            }
            return true;
        });
    }, [operations, period]);

    /* ============================================================
       CALCULS — Avec recompute des restes
       ============================================================ */
    const stats = useMemo(() => {
        let totalMontant = 0;
        let totalVerse = 0;
        let totalReste = 0;
        let totalAvanceInitiale = 0;
        let nbSoldes = 0;
        let nbPartiels = 0;
        let nbImpayes = 0;

        filteredOps.forEach(op => {
            const t = computeOperationTotals(op);
            totalMontant += t.montantInitial;
            totalVerse += t.totalVerse;
            totalReste += t.resteReel;
            totalAvanceInitiale += t.avanceInitiale;

            if (t.resteReel <= 0) nbSoldes++;
            else if (t.avanceInitiale > 0 || t.sommePaiements > 0) nbPartiels++;
            else nbImpayes++;
        });

        const nbOperations = filteredOps.length;
        const tauxRecouvrement = totalMontant > 0 ? (totalVerse / totalMontant) * 100 : 0;
        const pourcentageAvance = totalMontant > 0 ? (totalAvanceInitiale / totalMontant) * 100 : 0;

        return {
            totalMontant,
            totalVerse,
            totalReste,
            totalAvanceInitiale,
            nbOperations,
            nbSoldes,
            nbPartiels,
            nbImpayes,
            tauxRecouvrement,
            pourcentageAvance
        };
    }, [filteredOps]);

    const {
        totalMontant, totalVerse, totalReste, totalAvanceInitiale,
        nbOperations, nbSoldes, nbPartiels, nbImpayes,
        tauxRecouvrement, pourcentageAvance
    } = stats;

    /* ============================================================
       GRAPHIQUES
       ============================================================ */
    const montantsParMois = useMemo(() => {
        const moisMap = {};

        operations.forEach(op => {
            const date = parseMySQLDate(op.created_at);
            if (!date) return;

            const moisKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            const moisNom = date.toLocaleString('fr-FR', { month: 'short', year: '2-digit' });

            if (!moisMap[moisKey]) {
                moisMap[moisKey] = { mois: moisNom, total: 0, verse: 0, reste: 0, count: 0 };
            }

            const t = computeOperationTotals(op);
            moisMap[moisKey].total += t.montantInitial;
            moisMap[moisKey].verse += t.totalVerse;
            moisMap[moisKey].reste += t.resteReel;
            moisMap[moisKey].count++;
        });

        const sorted = Object.entries(moisMap)
            .sort((a, b) => a[0].localeCompare(b[0]))
            .slice(-6);

        return {
            labels: sorted.map(([_, d]) => d.mois),
            datasets: [
                {
                    label: 'Montant total',
                    data: sorted.map(([_, d]) => d.total),
                    backgroundColor: `${COLORS.primary}CC`,
                    borderColor: COLORS.primary,
                    borderWidth: 1,
                    borderRadius: 6,
                },
                {
                    label: 'Total versé',
                    data: sorted.map(([_, d]) => d.verse),
                    backgroundColor: `${COLORS.success}CC`,
                    borderColor: COLORS.success,
                    borderWidth: 1,
                    borderRadius: 6,
                },
                {
                    label: 'Reste à payer',
                    data: sorted.map(([_, d]) => d.reste),
                    backgroundColor: `${COLORS.danger}CC`,
                    borderColor: COLORS.danger,
                    borderWidth: 1,
                    borderRadius: 6,
                }
            ]
        };
    }, [operations]);

    const repartitionStats = useMemo(() => ({
        labels: ['Soldé', 'Avance partielle', 'Impayé'],
        datasets: [{
            data: [nbSoldes, nbPartiels, nbImpayes],
            backgroundColor: [COLORS.success, COLORS.warning, COLORS.danger],
            borderWidth: 0,
        }]
    }), [nbSoldes, nbPartiels, nbImpayes]);

    const hasRepartitionData = (nbSoldes + nbPartiels + nbImpayes) > 0;

    const topBeneficiaires = useMemo(() => {
        const benefMap = {};

        filteredOps.forEach(op => {
            const nom = `${op.beneficiaire_prenom || ''} ${op.beneficiaire_nom || ''}`.trim() || 'Inconnu';
            if (!benefMap[nom]) benefMap[nom] = { total: 0, verse: 0, reste: 0, count: 0 };

            const t = computeOperationTotals(op);
            benefMap[nom].total += t.montantInitial;
            benefMap[nom].verse += t.totalVerse;
            benefMap[nom].reste += t.resteReel;
            benefMap[nom].count++;
        });

        const sorted = Object.entries(benefMap)
            .sort((a, b) => b[1].total - a[1].total)
            .slice(0, 5);

        return {
            labels: sorted.map(([nom]) => nom.length > 20 ? nom.substring(0, 20) + '…' : nom),
            datasets: [{
                label: 'Montant total',
                data: sorted.map(([_, d]) => d.total),
                backgroundColor: `${COLORS.primary}CC`,
                borderColor: COLORS.primary,
                borderWidth: 1,
                borderRadius: 6,
            }]
        };
    }, [filteredOps]);

    /* ============================================================
       OPTIONS GRAPHIQUES
       ============================================================ */
    const barOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                labels: { font: { family: 'Inter, sans-serif', size: 11 }, color: '#475569', usePointStyle: true, padding: 15 }
            },
            tooltip: {
                backgroundColor: '#1e293b',
                padding: 12,
                cornerRadius: 8,
                titleFont: { family: 'Inter, sans-serif', size: 12 },
                bodyFont: { family: 'Inter, sans-serif', size: 11 },
                callbacks: {
                    label: function (context) {
                        let label = context.dataset.label || '';
                        if (label) label += ': ';
                        label += formatCurrency(context.raw);
                        return label;
                    }
                }
            }
        },
        scales: {
            x: { grid: { display: false }, ticks: { color: '#64748b', font: { family: 'Inter, sans-serif', size: 11 } } },
            y: {
                grid: { color: '#f1f5f9' },
                ticks: {
                    color: '#64748b',
                    font: { family: 'Inter, sans-serif', size: 10 },
                    callback: function (value) {
                        if (value >= 1000000) return (value / 1000000).toFixed(1) + 'M';
                        if (value >= 1000) return (value / 1000).toFixed(0) + 'k';
                        return value;
                    }
                }
            }
        }
    };

    const pieOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: { padding: 15, font: { family: 'Inter, sans-serif', size: 12 }, color: '#475569', usePointStyle: true }
            },
            tooltip: {
                backgroundColor: '#1e293b',
                padding: 12,
                cornerRadius: 8,
                callbacks: {
                    label: function (context) {
                        const label = context.label || '';
                        const value = context.raw || 0;
                        const total = context.dataset.data.reduce((a, b) => a + b, 0);
                        const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
                        return `${label}: ${value} opération(s) (${percentage}%)`;
                    }
                }
            }
        }
    };

    /* ============================================================
       LOADING
       ============================================================ */
    if (loading && operations.length === 0) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center" style={{ maxWidth: '400px' }}>
                            <div className="loading-ring-wrapper mb-4">
                                <div className="loading-ring"></div>
                                <div className="loading-ring-inner"></div>
                                <i className="ri-loader-4-fill loading-icon"></i>
                            </div>
                            <h5 className="fw-bold" style={{ color: COLORS.primaryDark, letterSpacing: '0.3px' }}>
                                Chargement des statistiques...
                            </h5>
                            <p className="text-muted small mb-0">Veuillez patienter quelques instants...</p>
                            <div className="mx-auto mt-4" style={{ width: '80%' }}>
                                <div className="progress" style={{ height: '3px', background: '#e9ecef', borderRadius: '10px', overflow: 'hidden' }}>
                                    <div
                                        className="progress-bar progress-bar-striped progress-bar-animated"
                                        style={{
                                            width: '100%',
                                            background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary}, ${COLORS.primary})`,
                                            backgroundSize: '200% 100%',
                                            animation: 'shimmer 1.5s linear infinite'
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
                <style>{`
                    .loading-ring-wrapper { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                    .loading-ring { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; border: 4px solid rgba(33,172,205,0.15); border-top-color: ${COLORS.primary}; animation: spin 1s linear infinite; }
                    .loading-ring-inner { position: absolute; top: 8px; left: 8px; right: 8px; bottom: 8px; border-radius: 50%; border: 3px solid rgba(33,172,205,0.08); border-bottom-color: ${COLORS.secondary}; animation: spin 0.8s linear infinite reverse; }
                    .loading-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: ${COLORS.primary}; animation: pulse-icon 1.2s ease-in-out infinite; }
                    @keyframes spin { to { transform: rotate(360deg); } }
                    @keyframes pulse-icon { 0%,100% { transform: translate(-50%,-50%) scale(1); opacity:1; } 50% { transform: translate(-50%,-50%) scale(1.15); opacity:0.6; } }
                    @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
                `}</style>
            </>
        );
    }

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-bar-chart-2-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Statistiques de caisse</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-bar-chart-line"></i>
                                        {period === 'month' ? 'Ce mois' : period === 'year' ? 'Cette année' : 'Tout'}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                        <i className="ri-checkbox-circle-line"></i> Soldés {nbSoldes}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.warning}15`, color: COLORS.warning }}>
                                        <i className="ri-time-line"></i> Partiels {nbPartiels}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.danger}15`, color: COLORS.danger }}>
                                        <i className="ri-error-warning-line"></i> Impayés {nbImpayes}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button className="btn-modern-outline" onClick={fetchOperations} disabled={loading}>
                                <i className={`ri-refresh-line ${loading ? 'spin-icon' : ''}`}></i> Actualiser
                            </Button>
                            <Button className="btn-modern-outline" onClick={() => navigate('/caisse/operations')}>
                                <i className="ri-history-line"></i> Historique
                            </Button>
                            <Button className="btn-modern-primary" onClick={() => navigate('/caisse/decaissement')}>
                                <i className="ri-add-line"></i> Nouveau décaissement
                            </Button>
                        </div>
                    </div>

                    {/* ===== ALERTE ERREUR ===== */}
                    {error && !loading && (
                        <Alert variant="danger" className="alert-modern mb-4" style={{ borderLeftColor: COLORS.danger, background: '#fef2f2' }}>
                            <div className="d-flex align-items-center">
                                <i className="ri-error-warning-line fs-4 me-3" style={{ color: COLORS.danger }}></i>
                                <div>
                                    <strong>Erreur de chargement</strong>
                                    <p className="mb-0 small">{error}</p>
                                </div>
                                <Button className="btn-modern-primary ms-auto" onClick={fetchOperations}>
                                    <i className="ri-refresh-line me-1"></i> Réessayer
                                </Button>
                            </div>
                        </Alert>
                    )}

                    {/* ===== ALERTE SI AUCUNE DONNÉE ===== */}
                    {operations.length === 0 && !loading && !error && (
                        <Alert variant="warning" className="alert-modern mb-4">
                            <div className="d-flex align-items-center">
                                <i className="ri-information-line fs-4 me-3"></i>
                                <div>
                                    <strong>Aucune opération de caisse</strong>
                                    <p className="mb-0 small">
                                        Commencez par créer un décaissement pour voir les statistiques.
                                    </p>
                                </div>
                                <Button
                                    className="btn-modern-primary ms-auto"
                                    onClick={() => navigate('/caisse/decaissement')}
                                >
                                    <i className="ri-add-line me-1"></i> Créer
                                </Button>
                            </div>
                        </Alert>
                    )}

                    {/* ===== KPI ===== */}
                    <Row className="g-3 mb-4">
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-primary">
                                <div className="kpi-icon-charte"><i className="ri-bank-card-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Total décaissé</span>
                                    <h3 className="kpi-value-charte">{formatCurrency(totalMontant)}</h3>
                                    <span className="kpi-unit-charte">{nbOperations} opération(s)</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-success">
                                <div className="kpi-icon-charte"><i className="ri-hand-coin-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Total versé</span>
                                    <h3 className="kpi-value-charte" style={{ color: COLORS.success }}>{formatCurrency(totalVerse)}</h3>
                                    <span className="kpi-unit-charte">{tauxRecouvrement.toFixed(1)}% du total</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-danger">
                                <div className="kpi-icon-charte"><i className="ri-alert-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Reste à payer</span>
                                    <h3 className="kpi-value-charte" style={{ color: COLORS.danger }}>{formatCurrency(totalReste)}</h3>
                                    <span className="kpi-unit-charte">Montant impayé</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-warning">
                                <div className="kpi-icon-charte"><i className="ri-percent-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Avances initiales</span>
                                    <h3 className="kpi-value-charte" style={{ color: COLORS.warning }}>{formatCurrency(totalAvanceInitiale)}</h3>
                                    <span className="kpi-unit-charte">{pourcentageAvance.toFixed(1)}% du total</span>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* ===== STAT CARDS ===== */}
                    <Row className="g-3 mb-4">
                        <Col sm="4">
                            <div className="stat-card-charte kpi-success">
                                <div className="stat-icon-charte" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                    <i className="ri-check-double-line"></i>
                                </div>
                                <h2 className="stat-value-charte" style={{ color: COLORS.success }}>{nbSoldes}</h2>
                                <small className="stat-label-charte">Opérations soldées</small>
                            </div>
                        </Col>
                        <Col sm="4">
                            <div className="stat-card-charte kpi-warning">
                                <div className="stat-icon-charte" style={{ background: `${COLORS.warning}15`, color: COLORS.warning }}>
                                    <i className="ri-time-line"></i>
                                </div>
                                <h2 className="stat-value-charte" style={{ color: COLORS.warning }}>{nbPartiels}</h2>
                                <small className="stat-label-charte">Avances partielles</small>
                            </div>
                        </Col>
                        <Col sm="4">
                            <div className="stat-card-charte kpi-danger">
                                <div className="stat-icon-charte" style={{ background: `${COLORS.danger}15`, color: COLORS.danger }}>
                                    <i className="ri-error-warning-line"></i>
                                </div>
                                <h2 className="stat-value-charte" style={{ color: COLORS.danger }}>{nbImpayes}</h2>
                                <small className="stat-label-charte">Opérations impayées</small>
                            </div>
                        </Col>
                    </Row>

                    {/* ===== SÉLECTEUR PÉRIODE ===== */}
                    <div className="d-flex justify-content-end mb-3">
                        <div className="period-selector-charte">
                            <button
                                className={`period-btn-charte ${period === 'month' ? 'active' : ''}`}
                                onClick={() => setPeriod('month')}
                            >
                                <i className="ri-calendar-line"></i> Ce mois
                            </button>
                            <button
                                className={`period-btn-charte ${period === 'year' ? 'active' : ''}`}
                                onClick={() => setPeriod('year')}
                            >
                                <i className="ri-calendar-2-line"></i> Cette année
                            </button>
                            <button
                                className={`period-btn-charte ${period === 'all' ? 'active' : ''}`}
                                onClick={() => setPeriod('all')}
                            >
                                <i className="ri-stack-line"></i> Tout
                            </button>
                        </div>
                    </div>

                    {/* ===== GRAPHIQUES ===== */}
                    <Row className="g-3 mb-4">
                        <Col lg={7}>
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="card-header-gradient">
                                    <h6 className="mb-0 fw-semibold text-white">
                                        <i className="ri-bar-chart-line me-2"></i>
                                        Évolution mensuelle des décaissements
                                    </h6>
                                </Card.Header>
                                <Card.Body className="p-3">
                                    <div style={{ height: '350px' }}>
                                        {montantsParMois.labels.length > 0 ? (
                                            <Bar data={montantsParMois} options={barOptions} />
                                        ) : (
                                            <div className="empty-state-charte">
                                                <div className="empty-state-icon-charte" style={{
                                                    background: `${COLORS.primary}10`, color: COLORS.primary
                                                }}>
                                                    <i className="ri-bar-chart-line"></i>
                                                </div>
                                                <p className="text-muted small mb-0">Aucune donnée à afficher</p>
                                            </div>
                                        )}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col lg={5}>
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="card-header-gradient-secondary">
                                    <h6 className="mb-0 fw-semibold text-white">
                                        <i className="ri-pie-chart-line me-2"></i>
                                        Répartition par statut
                                    </h6>
                                </Card.Header>
                                <Card.Body className="p-3">
                                    <div style={{ height: '300px' }}>
                                        {hasRepartitionData ? (
                                            <Pie data={repartitionStats} options={pieOptions} />
                                        ) : (
                                            <div className="empty-state-charte">
                                                <div className="empty-state-icon-charte" style={{
                                                    background: `${COLORS.secondary}10`, color: COLORS.secondary
                                                }}>
                                                    <i className="ri-pie-chart-line"></i>
                                                </div>
                                                <p className="text-muted small mb-0">Aucune donnée à afficher</p>
                                            </div>
                                        )}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* ===== TOP BÉNÉFICIAIRES ===== */}
                    <Row className="g-3">
                        <Col lg={12}>
                            <Card className="border-0 shadow-sm rounded-3">
                                <Card.Header className="card-header-gradient">
                                    <h6 className="mb-0 fw-semibold text-white">
                                        <i className="ri-user-star-line me-2"></i>
                                        Top 5 bénéficiaires ({period === 'month' ? 'ce mois' : period === 'year' ? 'cette année' : 'toutes opérations'})
                                    </h6>
                                </Card.Header>
                                <Card.Body className="p-3">
                                    <div style={{ height: '350px' }}>
                                        {topBeneficiaires.labels.length > 0 ? (
                                            <Bar data={topBeneficiaires} options={barOptions} />
                                        ) : (
                                            <div className="empty-state-charte">
                                                <div className="empty-state-icon-charte" style={{
                                                    background: `${COLORS.primary}10`, color: COLORS.primary
                                                }}>
                                                    <i className="ri-user-star-line"></i>
                                                </div>
                                                <p className="text-muted small mb-0">Aucun bénéficiaire à afficher</p>
                                            </div>
                                        )}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>
                <Footer />
            </div>

            {/* ===== STYLES ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                .spin-icon { animation: spin 1s linear infinite; }

                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }

                .kpi-charte {
                    background: white; border-radius: 12px;
                    padding: 1.2rem;
                    display: flex; align-items: center; gap: 1rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid;
                    transition: all 0.25s ease;
                    height: 100%;
                }
                .kpi-charte:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 28px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: var(--primary); }
                .kpi-success { border-left-color: var(--success); }
                .kpi-danger  { border-left-color: var(--danger); }
                .kpi-warning { border-left-color: var(--warning); }
                .kpi-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem; flex-shrink: 0;
                }
                .kpi-primary .kpi-icon-charte { background: ${COLORS.primary}15; color: var(--primary); }
                .kpi-success .kpi-icon-charte { background: ${COLORS.success}15; color: var(--success); }
                .kpi-danger  .kpi-icon-charte { background: ${COLORS.danger}15; color: var(--danger); }
                .kpi-warning .kpi-icon-charte { background: ${COLORS.warning}15; color: var(--warning); }
                .kpi-content-charte { flex: 1; min-width: 0; }
                .kpi-label-charte {
                    font-size: 0.7rem; color: #94a3b8;
                    text-transform: uppercase; font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value-charte {
                    font-size: 1.35rem; font-weight: 700;
                    margin: 0.3rem 0; color: #1e293b;
                }
                .kpi-unit-charte { font-size: 0.7rem; color: #94a3b8; }

                .stat-card-charte {
                    background: white; border-radius: 12px;
                    padding: 1.25rem;
                    text-align: center;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid;
                    transition: all 0.25s ease;
                    height: 100%;
                }
                .stat-card-charte:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 28px rgba(0,0,0,0.08);
                }
                .stat-card-charte.kpi-success { border-left-color: var(--success); }
                .stat-card-charte.kpi-warning { border-left-color: var(--warning); }
                .stat-card-charte.kpi-danger  { border-left-color: var(--danger); }
                .stat-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 0.75rem;
                    font-size: 1.4rem;
                }
                .stat-value-charte {
                    font-size: 1.75rem; font-weight: 700;
                    margin: 0.5rem 0 0.25rem;
                }
                .stat-label-charte {
                    color: #94a3b8; font-size: 0.8rem;
                }

                .period-selector-charte {
                    display: inline-flex;
                    background: white;
                    border-radius: 50px;
                    padding: 4px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
                    border: 1px solid #e2e8f0;
                }
                .period-btn-charte {
                    border: none;
                    background: transparent;
                    padding: 0.5rem 1rem;
                    border-radius: 50px;
                    font-size: 0.8rem;
                    font-weight: 500;
                    color: #64748b;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                }
                .period-btn-charte:hover {
                    color: var(--primary);
                    background: #f1f5f9;
                }
                .period-btn-charte.active {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    color: white;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                }

                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }
                .card-header-gradient-secondary {
                    background: linear-gradient(135deg, var(--secondary) 0%, #0d8ba6 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }

                .empty-state-charte {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    text-align: center;
                    height: 100%;
                    padding: 2rem;
                }
                .empty-state-icon-charte {
                    width: 70px; height: 70px;
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1rem;
                    font-size: 1.8rem;
                }

                .alert-modern {
                    border: none; border-radius: 12px;
                    padding: 1rem 1.25rem;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.06);
                    border-left: 4px solid var(--warning);
                    background: #fffbeb;
                }

                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white;
                }

                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .period-selector-charte { width: 100%; }
                    .period-btn-charte { flex: 1; justify-content: center; font-size: 0.7rem; padding: 0.5rem 0.5rem; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}